import { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { showMessageAction } from 'containers/Notification/actions';

interface UseUnsavedChangesOptions {
  hasChanges: boolean;
  message?: string;
}

export const useUnsavedChanges = ({
  hasChanges,
  message = 'You have unsaved changes. Are you sure you want to leave?',
}: UseUnsavedChangesOptions) => {
  const dispatch = useDispatch();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  // Browser navigation protection
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
      return undefined;
    };

    if (hasChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasChanges, message]);

  const confirmNavigation = useCallback(
    (callback: () => void) => {
      if (hasChanges) {
        pendingActionRef.current = callback;
        setShowConfirmDialog(true);
      } else {
        callback();
      }
    },
    [hasChanges]
  );

  const handleConfirm = useCallback(() => {
    setShowConfirmDialog(false);
    if (pendingActionRef.current) {
      pendingActionRef.current();
      pendingActionRef.current = null;
    }
  }, []);

  const handleCancel = useCallback(() => {
    setShowConfirmDialog(false);
    pendingActionRef.current = null;
    dispatch(showMessageAction('Changes preserved'));
  }, [dispatch]);

  return {
    confirmNavigation,
    showConfirmDialog,
    handleConfirm,
    handleCancel,
  };
};
